import { Program } from '@citruscamps/citrus-client'
import { faCircleHalfStroke, faGift, faSearch } from '@fortawesome/pro-regular-svg-icons'
import {
  faAddressBook,
  faBars,
  faCalendarWeek,
  faClipboardListCheck,
  faFileInvoiceDollar,
  faInbox,
  faMoon,
  faRunning,
  faSunBright,
  faTachometerAltFast,
  faUserFriends,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, Interval } from 'luxon'
import { default as React, useState } from 'react'
import { useQueryClient } from 'react-query'
import { NavLink } from 'react-router-dom'
import { ROUTES } from '../../../constants/routes'
import { AuthValue } from '../../../hooks/useAuth'
import { useRouter } from '../../../hooks/useRouter'
import { useToast } from '../../../hooks/useToast'
// import iconColour from '../../../images/icon-colour.svg'
// import iconColourDark from '../../../images/icon-colour-dark.svg'
import logoColourDark from '../../../images/logo-colour-dark.svg'
import logoColour from '../../../images/logo-colour.svg'
import translations from '../../../translations/en.json'
import { CommandKeys } from '../../CommandMenu/CommandMenu'
import { useCommandMenuOpen } from '../../CommandMenu/hooks/useCommandMenuOpen'
import { useFlags } from '../../FeatureFlag/hooks/useFlags'
import { Icon } from '../../Icon/Icon'
import { Tooltip } from '../../Tooltip/Tooltip'
import { TruncatedText } from '../../TruncatedText/TruncatedText'
import { getTheme, setTheme } from '../hooks/theme'
import { useFetchCitrusPromotion } from '../hooks/useFetchCitrusPromotions'
import { SideNavSkeleton } from './LayoutSkeleton'
import { MenuItem, MenuItemProps, hasItemRequirements } from './MenuItem'
import { ReferralModal } from './ReferralModal'
import { UserDropdown } from './UserDropdown'

const isProduction = process.env.REACT_APP_IS_PRODUCTION === 'true'

export const SideNav = ({
  user,
  member,
  setProgramId,
  isLoading,
  isSwitching,
}: React.PropsWithChildren<AuthValue>): React.ReactElement => {
  const { setIsOpen } = useCommandMenuOpen()
  const { flags } = useFlags()
  const program: Program | undefined = member?.program
  const queryClient = useQueryClient()
  const { query } = useRouter()
  const isShowingReferral = !!query.referral
  const [showCollapsed, setShowCollasped] = useState<boolean>(false)
  const [clicks, setClicks] = useState<number>(0)
  const [isReferralModalOpen, setIsReferralModalOpen] = useState<boolean>(isShowingReferral)
  const { data: citrusPromo } = useFetchCitrusPromotion({ program })
  const { referral } = citrusPromo || {}
  const referalAmount: string | undefined = referral?.display?.split('.').shift()
  const { setToast } = useToast()
  const daysLeftInTrial: number = program?.billing?.trial_end
    ? Math.round(
        Interval.fromDateTimes(
          DateTime.now(),
          DateTime.fromJSDate(program.billing.trial_end),
        ).toDuration('days').days,
      )
    : 0
  const isPastDue = program?.billing?.status === 'past_due'
  const isTrialing =
    program?.billing?.trial_start &&
    program?.billing?.trial_end &&
    program?.billing?.trial_start <= new Date() &&
    program?.billing?.trial_end >= new Date() &&
    daysLeftInTrial <= 30

  const menuItems: MenuItemProps[] = [
    {
      text: 'Dashboard',
      icon: faTachometerAltFast,
      route: ROUTES.DASHBOARD,
    },
    {
      text: 'Team',
      icon: faUserFriends,
      route: ROUTES.TEAM,
    },
    {
      text: 'Financials',
      icon: faFileInvoiceDollar,
      route: ROUTES.FINANCIALS,
    },
    {
      text: 'Events',
      icon: faCalendarWeek,
      route: ROUTES.EVENTS,
    },
    {
      text: 'Contacts',
      icon: faAddressBook,
      route: ROUTES.CONTACTS,
    },
    {
      text: 'Attendees',
      icon: faRunning,
      route: ROUTES.ATTENDEES,
    },
    {
      text: 'Inbox',
      icon: faInbox,
      route: ROUTES.INBOX,
      requires: ['communications_enabled'],
    },
    {
      text: 'Marketing',
      icon: faClipboardListCheck,
      route: ROUTES.MARKETING,
    },
    // {
    //   text: 'Tasks',
    //   icon: faClipboardListCheck,
    //   route: ROUTES.TASKS,
    //   disabled: true,
    // },
    // {
    //   title: 'Partners',
    //   icon: faHandsHelping,
    //   route: ROUTES.PARTNERS,
    //   disabled: true,
    // },
  ]

  if (isLoading || isSwitching || !user) {
    return <SideNavSkeleton />
  }

  return (
    <nav
      className={
        'navbar navbar-vertical fixed-left navbar-expand-md shadow-sm row g-0 bg-body-tertiary py-2 px-1 p-md-4 border-end border-body-secondary' +
        (isProduction ? '' : ' navbar-test')
      }
      id="sidebar"
    >
      <div className="container-fluid">
        <button
          type="button"
          className="navbar-toggler border-0 text-body"
          onClick={(): void => {
            setShowCollasped(!showCollapsed)
          }}
          data-bs-toggle="collapse"
          data-bs-target="#sidebarNavigation"
          aria-controls="sidebarNavigation"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <NavLink className="navbar-brand d-md-none m-0" to={ROUTES.DASHBOARD}>
          <img src={logoColour} className="navbar-brand-img mx-auto dark-only" alt="Citrus" />
          <img src={logoColourDark} className="navbar-brand-img mx-auto light-only" alt="Citrus" />
        </NavLink>
        <div className="navbar-user d-md-none">
          <div className="dropdown">
            <UserDropdown user={user} member={member} setProgramId={setProgramId} isMobile={true} />
          </div>
        </div>
        <div
          className={['collapse navbar-collapse', showCollapsed ? 'show' : ''].join(' ')}
          id="sidebarNavigation"
        >
          <a
            href="/"
            className="d-none d-md-flex align-items-center pe-auto link-body-emphasis bg-body-secondary rounded-3 p-3 mb-3"
          >
            <Icon
              src={program?.picture_url}
              alt={program?.name}
              className="bg-white rounded me-3"
              style={{ minWidth: '40px', height: '40px' }}
              placeholderColor={program?.preferences?.brand_accent_color}
            />
            <TruncatedText lines={2}>{program?.name}</TruncatedText>
          </a>
          <ul className="navbar-nav">
            {menuItems.map((item: MenuItemProps): React.ReactElement | null =>
              !hasItemRequirements({ item, flags }) && item.isBeta ? null : (
                <MenuItem
                  key={item.route}
                  {...{
                    ...item,
                    disabled: item.disabled || !program,
                    text: item.text,
                    isUpgrade: !hasItemRequirements({ item, flags }),
                  }}
                />
              ),
            )}
          </ul>
          <div className="mt-auto" />
          <div className="navbar-brand d-none d-md-block my-1 pb-2">
            {isPastDue && (
              <div>
                <Tooltip title="Your account is past due. Please update your payment information to avoid service interruption.">
                  <span className="badge bg-danger mb-3 shadow-sm" style={{ fontSize: '0.75rem' }}>
                    Subscription past due
                  </span>
                </Tooltip>
              </div>
            )}
            {isTrialing && !!program?.billing?.trial_end && (
              <div>
                <span
                  className="badge text-bg-light mb-3 shadow-sm"
                  style={{ fontSize: '0.75rem' }}
                >
                  Trial ends {DateTime.fromJSDate(program?.billing?.trial_end).toRelative()}
                </span>
              </div>
            )}
            <div className="text-center">
              <div>
                <button
                  type="button"
                  className="btn btn-primary border-0 shadow-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => {
                    setIsReferralModalOpen(true)
                  }}
                >
                  <FontAwesomeIcon icon={faGift} className="me-2" /> Invite other programs
                </button>
              </div>
              <div className="text-body-secondary mb-3 mt-2" style={{ fontSize: '0.75rem' }}>
                You get {referalAmount}
              </div>
              {isReferralModalOpen && program && (
                <ReferralModal program={program} onClose={() => setIsReferralModalOpen(false)} />
              )}
            </div>
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setIsOpen(true)}
              >
                <FontAwesomeIcon icon={faSearch} className="me-2" /> Search
                <span className="ms-2">
                  <CommandKeys />
                </span>
              </button>
            </div>
            <img
              src={logoColour}
              className="navbar-brand-img mx-auto dark-only"
              alt="Citrus"
              onClick={() => {
                if (clicks > 5) {
                  queryClient.clear()
                  setClicks(0)
                  setToast({
                    header: (translations as any)['success.default_toast_header'],
                    message: (translations as any)['success.default_toast_message'],
                    type: 'success',
                  })
                } else {
                  setClicks(clicks + 1)
                }
              }}
            />
            <img
              src={logoColourDark}
              className="navbar-brand-img mx-auto light-only"
              alt="Citrus"
              onClick={() => {
                if (clicks > 5) {
                  queryClient.clear()
                  setClicks(0)
                  setToast({
                    header: (translations as any)['success.default_toast_header'],
                    message: (translations as any)['success.default_toast_message'],
                    type: 'success',
                  })
                } else {
                  setClicks(clicks + 1)
                }
              }}
            />
            <div className="text-body small">
              <a className="text-body small" href="https://www.joincitrus.com/terms-of-service">
                Terms
              </a>
              <span className="mx-2">·</span>
              <a className="text-body small" href="https://www.joincitrus.com/privacy-policy">
                Privacy
              </a>
            </div>
          </div>
          <ThemeToggle />
          <div className="navbar-user d-none d-md-flex" id="sidebarUser">
            <div className="dropup w-100">
              <UserDropdown user={user} member={member} setProgramId={setProgramId} />
            </div>
          </div>
        </div>
      </div>
      {/* TODO: New test flag */}
      {/* {!isProduction && <div className="navbar-test-flag d-md-none"></div>} */}
    </nav>
  )
}

const ThemeToggle = (): React.ReactElement => {
  return (
    <div className="text-center">
      <div className="btn-group">
        <input
          id="light-theme"
          name="theme-selector"
          type="radio"
          className="btn-check"
          defaultChecked={getTheme() === 'light'}
          onClick={() => setTheme('light')}
        />
        <label
          title="Light theme"
          className="btn btn-sm btn-body-secondary border-bottom-0"
          htmlFor="light-theme"
          style={{ '--bs-btn-border-radius': '20px', borderBottomLeftRadius: 0 }}
        >
          <FontAwesomeIcon icon={faSunBright} />
        </label>
        <input
          id="auto-theme"
          name="theme-selector"
          type="radio"
          className="btn-check"
          defaultChecked={getTheme() === 'auto'}
          onClick={() => setTheme('auto')}
        />
        <label
          title="Automatic theme"
          className="btn btn-sm btn-body-secondary border-bottom-0"
          htmlFor="auto-theme"
        >
          <FontAwesomeIcon icon={faCircleHalfStroke} />
        </label>
        <input
          id="dark-theme"
          name="theme-selector"
          type="radio"
          className="btn-check"
          defaultChecked={getTheme() === 'dark'}
          onClick={() => setTheme('dark')}
        />
        <label
          title="Dark theme"
          className="btn btn-sm btn-body-secondary border-bottom-0"
          htmlFor="dark-theme"
          style={{ '--bs-btn-border-radius': '20px', borderBottomRightRadius: 0 }}
        >
          <FontAwesomeIcon icon={faMoon} />
        </label>
      </div>
    </div>
  )
}
